import {
  createTheme,
  FormControl,
  Paper,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Tooltip, CircleMarker, Polygon, Polyline, SVGOverlay, useMap } from 'react-leaflet';
import "leaflet-polylinedecorator";

import { selectRTFO, setCurrentStageName, setIsLoadingData } from '../../reducers/rtfoSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/styles';

import * as turf from '@turf/turf';
import RTFOPressureAnalysisMapLegend from './RTFOPressureAnalysisMapLegend';
import { WellLabel } from './RTFOMapWithWellbore';
import { stripMonitorUWI } from './rtfoConfig';
import ScrollZoomControl from './ScrollZoomControl';
import ScrollZoomControlOverlay from './ScrollZoomControlOverlay';
import { rtfoConversionFactors } from './rtfoConversionFactors';
import NewLabel from './NewLabel';
import RTFOWellbore3D from './RTFOWellbore3D';

export const RTFOPressureAnalysisMap = ({ currentWellName, monitorUWI, setMonitorUWI, monitorWellEvents, fiberData, updateMonitorWellEvents, shouldUpdateMonitorWellEvents, updateMonitorWellEventsMap, gridView, darkMode }) => {

  const theme = useTheme();
  const dispatch = useDispatch();

  const mapRef = useRef(null);
  const containerRef = useRef(null);

  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStageName = currentRTFO?.currentStageName;
  const currentWell = currentStageName?.split('_')?.[1]?.toLowerCase();
  const units = currentRTFO?.settings?.units || 'metric';

  const [scrollZoomControlOverlayVisible, setScrollZoomControlOverlayVisible] = useState(false);
  const [displayType, setDisplayType] = useState("2d");

  let formattedMonitorUWI = monitorUWI;
  if (formattedMonitorUWI?.includes('/')) {
    formattedMonitorUWI = formattedMonitorUWI?.split('/');
    formattedMonitorUWI = `${formattedMonitorUWI?.[0]}-${formattedMonitorUWI?.[1]?.split('-')?.slice(0, 2)?.join('-')}`;
  };

  const allStages = useMemo(() => {
    try {
      let allStages = [];

      for (const well in currentRTFO?.perfSchedule?.wells) {
        for (const stageNumber in currentRTFO?.perfSchedule?.wells?.[well]?.stages) {
          const stageName = `${currentRTFO?.name}_${well?.charAt(0)?.toUpperCase()?.concat(well?.substring(1,))}_${stageNumber}`;
          allStages.push(stageName);
        };
      };

      allStages?.sort((a, b) => (currentRTFO?.stageData?.[a]?.stageStartTimestamp || 0) - (currentRTFO?.stageData?.[b]?.stageStartTimestamp || 0));

      return allStages;

    } catch (error) {
      return [];
    };
  }, [currentRTFO?.name, currentRTFO?.perfSchedule?.wells, currentRTFO?.stageData]);

  const wellCoordinates = useMemo(() => {
    try {
      const coords = {};
      const wells = currentRTFO?.surveys?.wells && Object.keys(currentRTFO?.surveys?.wells);

      if (!wells?.length) return coords;

      for (let i = 0; i < wells.length; i++) { // calculate well paths
        const lat = currentRTFO?.surveys?.wells?.[wells[i]]?.surfaceLat;
        const lon = currentRTFO?.surveys?.wells?.[wells[i]]?.surfaceLon;
        const surveyData = currentRTFO?.surveys?.wells?.[wells[i]]?.surveyData;
        const wellCoordinates = surveyToCoordinates(lat, lon, surveyData);

        if (!coords[wells[i]]) coords[wells[i]] = [];

        coords[wells[i]] = wellCoordinates;
      };

      return coords;

    } catch (error) {
      console.log(error);
      return {};
    };
  }, [currentRTFO?.surveys?.wells]);

  const monitorWellCoordinates = useMemo(() => {
    try {
      const coords = {};
      const wells = currentRTFO?.monitorSurveys && Object.keys(currentRTFO?.monitorSurveys);

      if (!wells?.length) return coords;

      for (let i = 0; i < wells.length; i++) { // calculate well paths
        if (!currentRTFO?.monitorSurveys?.[wells[i]]?.surveyData) continue;

        const lat = currentRTFO?.monitorSurveys?.[wells[i]]?.surfaceLat;
        const lon = currentRTFO?.monitorSurveys?.[wells[i]]?.surfaceLon;
        const surveyData = currentRTFO?.monitorSurveys?.[wells[i]]?.surveyData;
        const wellCoordinates = surveyToCoordinates(lat, lon, surveyData);

        const monitorName = wells[i]?.charAt(0)?.toUpperCase()?.concat(wells[i]?.substring(1,));

        if (!coords[monitorName]) coords[monitorName] = [];

        coords[monitorName] = wellCoordinates;
      };

      return coords;

    } catch (error) {
      console.log(error);
      return {};
    };
  }, [currentRTFO?.monitorSurveys]);

  const clusterCoordinates3D = useMemo(() => {
    try {
      const coords = {};

      for (let j = 0; j < allStages?.length; j++) { // calculate stage/cluster lines

        const stageName = allStages[j];
        const stageNumber = String(stageName?.split('_')?.[2])?.replace(/[^0-9]+/g, '');
        const wellName = stageName?.split('_')?.[1]?.toLowerCase();
        if (!currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]) continue;

        const fracLength = 300;

        const clusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters ? Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters) : [];

        for (let k = 0; k < clusters?.length; k++) {
          const clusterName = clusters[k];
          const md = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters?.[clusters[k]]?.shotDepth;
          const surveyData = currentRTFO?.surveys?.wells?.[wellName]?.surveyData;
          const wellLatLngs = wellCoordinates[wellName]?.map(c => [c.lat, c.lon]);
          const clusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, md);
          let clusterLineCoordinates = calculateLineCoordinates(clusterLatLon, fracLength);
          clusterLineCoordinates = rotatePolyline(clusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

          // let clusterLineCoordinates = getLineCoordinates(clusterLatLon, fracLength);
          // clusterLineCoordinates = rotateLine(clusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

          if (!coords[stageName]) coords[stageName] = {};

          coords[stageName][clusterName] = {
            top: { // top of the frac plane
              start: { // start of the frac line along the plane top
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              },
              center: { // center of the frac line along the plane top
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              },
              end: { // end of the frac line along the plane top
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              }
            },
            center: { // center of the frac plane (corresponds to stage TVD)
              start: {
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md)
              },
              center: {
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md)
              },
              end: {
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md)
              }
            },
            bottom: {
              start: {
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              },
              center: {
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              },
              end: {
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              }
            }
          };
        };
      };

      return coords;

    } catch (error) {
      console.log(error);
      return {};
    };

  }, [allStages, currentRTFO?.settings, currentRTFO?.perfSchedule?.wells, currentRTFO?.surveys?.wells, wellCoordinates]);

  const stageCoordinates3D = useMemo(() => {
    try {
      const coords = {};

      Object.keys(clusterCoordinates3D)?.forEach(stageName => {
        const clusterCoords = Object.values(clusterCoordinates3D[stageName]);

        const firstCluster = clusterCoords?.[0]?.center; // coordinates of the first stage cluster at center tvd
        const lastCluster = clusterCoords?.[clusterCoords?.length - 1]?.center; // coordinates of the last stage cluster at center tvd
        const startLatLon = [[firstCluster?.start?.lat, firstCluster?.start?.lon], [firstCluster?.center?.lat, firstCluster?.center?.lon], [firstCluster?.end?.lat, firstCluster?.end?.lon]];
        const endLatLon = [[lastCluster?.start?.lat, lastCluster?.start?.lon], [lastCluster?.center?.lat, lastCluster?.center?.lon], [lastCluster?.end?.lat, lastCluster?.end?.lon]];

        const averageTVD = (firstCluster?.center?.tvd + lastCluster?.center?.tvd) / 2;

        const stageLatLon = getMiddlePoint([startLatLon, endLatLon]); // coordinates of the center of the stage

        const fracLength = 300;

        let stageLineCoordinates = calculateLineCoordinates(stageLatLon, fracLength);
        stageLineCoordinates = rotatePolyline(stageLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

        coords[stageName] = {
          top: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            }
          },
          center: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD
            }
          },
          bottom: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            }
          }
        };
      });

      return coords;

    } catch (error) {
      console.log(error);
      return {};
    };

  }, [clusterCoordinates3D, currentRTFO?.settings]);

  // Get a list of stages with FDI events
  const stagesWithEvents = useMemo(() => {
    const filteredEvents = monitorWellEvents?.filter(e => e.name !== 'Stage Start');

    const stageNames = [...new Set(filteredEvents?.map(e => (e.stageName)))];

    return stageNames;
  }, [monitorWellEvents]);


  const monitorWellsWithEvents = useMemo(() => {
    const filteredEvents = monitorWellEvents?.filter(e => e.name !== 'Stage Start');

    const monitorNames = [...new Set(filteredEvents?.map(e => (e.monitorUWI))?.filter(d => d?.length > 0))];

    return monitorNames;
  }, [monitorWellEvents]);

  // CALCULATE INTERSECTION LINE COORDINATES
  const allStagesIntersectionLineCoordinates = useMemo(() => {

    const intersectionCoordinates = {};

    for (const stageName of stagesWithEvents) {
      const stageLatLon = [stageCoordinates3D?.[stageName]?.center?.center?.lat, stageCoordinates3D?.[stageName]?.center?.center?.lon];
      let stageLineCoordinates = calculateLineCoordinates(stageLatLon, 5000);
      stageLineCoordinates = rotatePolyline(stageLineCoordinates, (currentRTFO?.settings?.fracAngle || 45)); // use 5 km line to find intersections      
      intersectionCoordinates[stageName] = stageLineCoordinates;
    };

    return intersectionCoordinates;
  }, [currentRTFO?.settings?.fracAngle, stageCoordinates3D, stagesWithEvents]);

  // CALCULATE GLOBAL MAP CENTER
  const globalCenter = useMemo(() => {
    try {
      return calculateCenterPoint(Object.keys(stageCoordinates3D)?.map(s => stageCoordinates3D[s]?.center?.center)?.flat(Infinity));
    } catch (error) {
      console.log(error);
      return { lat: 0, lon: 0 };
    };
  }, [stageCoordinates3D]);

  const stagePolygonsCoordinates = useMemo(() => {
    try {

      const allPolygonsCoordinates = {};

      if (!stagesWithEvents) return {};

      for (const stageName of currentRTFO?.stages) {
      // for (const stageName of stagesWithEvents) {
        if (!clusterCoordinates3D?.[stageName]) continue;

        const stageClusters = Object.keys(clusterCoordinates3D?.[stageName]);
        const firstCluster = stageClusters?.[0];
        const lastCluster = stageClusters?.[stageClusters?.length - 1];

        if (firstCluster !== lastCluster) { // if multi-cluster stage
          // Top start, top end, bottom end, bottom start (swap lon and lat)
          const stagePolygonCoordinates = [
            [clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.start?.lat, clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.start?.lon],
            [clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.end?.lat, clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.end?.lon],
            [clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.end?.lat, clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.end?.lon],
            [clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.start?.lat, clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.start?.lon]
          ];
          allPolygonsCoordinates[stageName] = stagePolygonCoordinates;
        } else { // if single-cluster stage
          const trimmedStageNumber = String(Number(String(stageName?.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));
          const wellName = stageName?.split('_')?.[1]?.toLowerCase();
          const lastStageNumber = Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages)?.slice(-1)?.[0];
          const currentStageDepth = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[trimmedStageNumber]?.clusters?.['1']?.shotDepth;
          const surveyData = currentRTFO?.surveys?.wells?.[wellName]?.surveyData;
          const wellLatLngs = wellCoordinates[wellName]?.map(c => [c.lat, c.lon]);

          let stageLength = 0;

          if (trimmedStageNumber !== lastStageNumber) {
            const nextStageDepth = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[Number(trimmedStageNumber) + 1]?.clusters?.['1']?.shotDepth;
            stageLength = currentStageDepth - nextStageDepth;
          } else { // for the last stage
            const previousStageDepth = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[Number(trimmedStageNumber) - 1]?.clusters?.['1']?.shotDepth;
            stageLength = previousStageDepth - currentStageDepth;
          };

          const fracLength = 300;

          if (stageLength > 0) {
            // For bottom pseudo-cluster line
            const firstClusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, currentStageDepth - stageLength / 3);
            let firstClusterLineCoordinates = calculateLineCoordinates(firstClusterLatLon, fracLength);
            firstClusterLineCoordinates = rotatePolyline(firstClusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

            // For top pseudo-cluster line
            const lastClusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, currentStageDepth + stageLength / 3);
            let lastClusterLineCoordinates = calculateLineCoordinates(lastClusterLatLon, fracLength);
            lastClusterLineCoordinates = rotatePolyline(lastClusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));

            const stagePolygonCoordinates = [
              [firstClusterLineCoordinates?.[0]?.[0], firstClusterLineCoordinates?.[0]?.[1]],
              [firstClusterLineCoordinates?.[2]?.[0], firstClusterLineCoordinates?.[2]?.[1]],
              [lastClusterLineCoordinates?.[2]?.[0], lastClusterLineCoordinates?.[2]?.[1]],
              [lastClusterLineCoordinates?.[0]?.[0], lastClusterLineCoordinates?.[0]?.[1]]
            ];
            allPolygonsCoordinates[stageName] = stagePolygonCoordinates;
          };

        };

      };

      return allPolygonsCoordinates;

    } catch (error) {
      console.log(error);
      return null;
    };
  }, [stagesWithEvents, currentRTFO?.stages, currentRTFO?.perfSchedule?.wells, currentRTFO?.surveys?.wells, currentRTFO?.settings?.fracAngle, clusterCoordinates3D, wellCoordinates]);

  const allMonitorWellsIntersectionCoordinates = useMemo(() => {
    const intersectionCoordinates = {};

    if (!monitorWellsWithEvents) return {};

    for (const monitorName of monitorWellsWithEvents) {
      const monitorStages = [...new Set(monitorWellEvents?.filter(e => e.name !== 'Stage Start' && e.monitorUWI === monitorName)?.map(e => e.stageName))];
      const monitorCoordinates = {};
      const formattedMonitorUWI = Object.keys(currentRTFO?.monitorSurveys)?.find(uwi => currentRTFO?.client === 'marathon' ? uwi === monitorName : stripMonitorUWI(uwi) === stripMonitorUWI(monitorName)) || monitorName;
      // const formattedMonitorUWI = Object.keys(currentRTFO?.monitorSurveys)?.find(uwi => stripMonitorUWI(uwi) === stripMonitorUWI(monitorName)) || monitorName;

      // if (formattedMonitorUWI?.includes('/')) {
      //   formattedMonitorUWI = monitorName?.split('/');
      //   formattedMonitorUWI = `${formattedMonitorUWI?.[0]}-${formattedMonitorUWI?.[1]?.split('-')?.slice(0, 2)?.join('-')}`;
      // };

      for (const stageName of monitorStages) {
        const intersectionCoordinates = findIntersectionCoordinates(allStagesIntersectionLineCoordinates?.[stageName], monitorWellCoordinates?.[formattedMonitorUWI], currentRTFO?.monitorSurveys?.[formattedMonitorUWI]?.surveyData);
        if (!Math.abs(intersectionCoordinates?.[0]) > 0 || !Math.abs(intersectionCoordinates?.[1]) > 0) continue;
        monitorCoordinates[stageName] = intersectionCoordinates;
      };

      if (Object.keys(monitorCoordinates)?.length > 0) {
        intersectionCoordinates[monitorName] = monitorCoordinates;
      };
    };

    return intersectionCoordinates;
  }, [allStagesIntersectionLineCoordinates, currentRTFO?.client, currentRTFO?.monitorSurveys, monitorWellCoordinates, monitorWellEvents, monitorWellsWithEvents]);

  const allMonitorWellsIntersectionMD = useMemo(() => {
    const intersectionMD = {};

    if (!monitorWellsWithEvents) return {};

    for (const monitorName of monitorWellsWithEvents) {
      const monitorStages = [...new Set(monitorWellEvents?.filter(e => e.name !== 'Stage Start' && e.monitorUWI === monitorName)?.map(e => e.stageName))];
      const monitorMD = {};

      for (const stageName of monitorStages) {
        monitorMD[stageName] = findIntersectionMD(allMonitorWellsIntersectionCoordinates?.[monitorName]?.[stageName], monitorWellCoordinates?.[monitorName], currentRTFO?.monitorSurveys?.[monitorName]?.surveyData);
      };

      intersectionMD[monitorName] = monitorMD;
    };

    return intersectionMD;
  }, [allMonitorWellsIntersectionCoordinates, currentRTFO?.monitorSurveys, monitorWellCoordinates, monitorWellEvents, monitorWellsWithEvents]);

  const allInterferenceLineCoordinates = useMemo(() => {
    const interferenceCoordinates = {};

    if (!monitorWellsWithEvents) return {};

    for (const monitorName of monitorWellsWithEvents) {

      const monitorStages = [...new Set(monitorWellEvents?.filter(e => e.name !== 'Stage Start' && e.monitorUWI === monitorName)?.map(e => e.stageName))];
      const monitorCoordinates = {};

      for (const stageName of monitorStages) {
        const interferenceLineCoordinates = [[stageCoordinates3D[stageName]?.center?.center?.lat, stageCoordinates3D[stageName]?.center?.center?.lon], allMonitorWellsIntersectionCoordinates?.[monitorName]?.[stageName]];
        if (!Math.abs(interferenceLineCoordinates?.[0]?.[0]) > 0 || !Math.abs(interferenceLineCoordinates?.[0]?.[1]) > 0 || !Math.abs(interferenceLineCoordinates?.[1]?.[0]) > 0 || !Math.abs(interferenceLineCoordinates?.[1]?.[1]) > 0) continue;
        monitorCoordinates[stageName] = interferenceLineCoordinates;
      };

      if (Object.keys(monitorCoordinates)?.length > 0) {
        interferenceCoordinates[monitorName] = monitorCoordinates;
      };

    };

    return interferenceCoordinates;
  }, [allMonitorWellsIntersectionCoordinates, monitorWellEvents, monitorWellsWithEvents, stageCoordinates3D]);

  const allInterferenceLineDistances = useMemo(() => {
    const lineDistances = {};

    if (!monitorWellsWithEvents) return {};

    for (const monitorName of monitorWellsWithEvents) {
      const monitorStages = [...new Set(monitorWellEvents.filter(e => e.name !== 'Stage Start' && e.monitorUWI === monitorName)?.map(e => e.stageName))];
      const monitorDistances = {};

      for (const stageName of monitorStages) {
        monitorDistances[stageName] = (!isNaN(allInterferenceLineCoordinates[monitorName]?.[stageName]?.[0]?.[0]) && !isNaN(allInterferenceLineCoordinates[monitorName]?.[stageName]?.[0]?.[1]) && !isNaN(allInterferenceLineCoordinates[monitorName]?.[stageName]?.[1]?.[0]) && !isNaN(allInterferenceLineCoordinates[monitorName]?.[stageName]?.[1]?.[1])) ? calculateDistance(allInterferenceLineCoordinates[monitorName]?.[stageName]?.[0], allInterferenceLineCoordinates[monitorName]?.[stageName]?.[1]) : 0;
      };

      lineDistances[monitorName] = monitorDistances;
    };

    return lineDistances;
  }, [allInterferenceLineCoordinates, monitorWellEvents, monitorWellsWithEvents]);

  const fiberEventsCoordinates = useMemo(() => {
    try {
      if (!fiberData?.length > 0) return {};

      const eventsCoordinates = {};

      fiberData?.forEach(event => {
        const stageName = event.stageName;
        const stageCoordinates = clusterCoordinates3D[stageName];
        const stageClusters = Object.keys(stageCoordinates);

        const stageBottom = [stageCoordinates[stageClusters[0]]?.center?.center?.lat, stageCoordinates[stageClusters[0]]?.center?.center?.lon];
        const stageTop = [stageCoordinates[stageClusters[stageClusters?.length - 1]]?.center?.center?.lat, stageCoordinates[stageClusters[stageClusters?.length - 1]]?.center?.center?.lon];
        const eventBottom = event.bottomLatLon;
        const eventCenter = event.centerLatLon;
        const eventTop = event.topLatLon;

        if ((currentWellName === 'All' || currentWellName === event.wellName) && event.monitorName === monitorUWI) {
          // Top start, top end, bottom end, bottom start (swap lon and lat)
          eventsCoordinates[event.eventId] = {
            'narrow': [
              stageBottom,
              stageTop,
              eventCenter,
              eventCenter
            ],
            'wide': [
              stageBottom,
              stageTop,
              eventTop,
              eventBottom
            ]
          }
        };
      })

      return eventsCoordinates;

    } catch (error) {
      console.log(error);
      return {}
    };
  }, [clusterCoordinates3D, currentWellName, fiberData, monitorUWI]);


  useEffect(() => {
    const map = mapRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (map) {
        map.invalidateSize();
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    };

    return () => {
      resizeObserver.disconnect();
    };
  }, [gridView]);


  useEffect(() => {
    updateMonitorWellEventsMap(true);
  }, [currentStageName, monitorUWI, updateMonitorWellEventsMap]);

  useEffect(() => {
    if (monitorWellEvents?.length === 0) return;

    const events = JSON.parse(JSON.stringify(monitorWellEvents?.filter(e => e.name !== 'Stage Start')));
    // const events = JSON.parse(JSON.stringify(monitorWellEvents?.filter(e => e.name !== 'Stage Start' && stripMonitorUWI(e?.monitorUWI) === stripMonitorUWI(monitorUWI))));

    let allMonitorWellEvents = JSON.parse(JSON.stringify(monitorWellEvents));

    if (shouldUpdateMonitorWellEvents?.current) {

      console.log('Updating events');

      for (let i = 0; i < events?.length; i++) {
        const event = events[i];
        const distance = allInterferenceLineDistances?.[event?.monitorUWI]?.[event?.stageName];

        event.distance = distance || 0;
        event.diffusivity = event?.slurryVolume > 1 && event.distance > 0 ? Math.pow(distance, 2) / (4 * Math.PI * event?.slurryVolume) : 0; // this uses slurry volume at the event time
        if (isNaN(event.diffusivity) || !isFinite(event.diffusivity)) event.diffusivity = 0;

        for (let j = 0; j < allMonitorWellEvents?.length; j++) {
          if (event.timestamp === allMonitorWellEvents[j]?.timestamp && event.stageName === allMonitorWellEvents[j]?.stageName && event.monitorUWI === allMonitorWellEvents[j]?.monitorUWI) {
            allMonitorWellEvents[j] = event;
          };
        };
      };

      if (allMonitorWellEvents?.length > 0) {
        updateMonitorWellEvents(allMonitorWellEvents);
        updateMonitorWellEventsMap(false);
      };

    };

    //eslint-disable-next-line
  }, [monitorUWI, monitorWellEvents, updateMonitorWellEvents]);

  const arrow = [
    {
      offset: "100%",
      repeat: 0,
      symbol: L.Symbol.arrowHead({
        pixelSize: 10,
        polygon: false,
        pathOptions: { stroke: true, color: 'tomato' }
      })
    }
  ];

  const darkTheme = createTheme({
    palette: {
      mode: "dark"
    }
  });

  try {
    return (
      <Paper
        style={{
          margin: '0',
          padding: 0,
          backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')],
          position: 'relative',
          width: '100%',
          height: gridView ? 580 : 940
        }}
        elevation={0}
        ref={containerRef}
      >
        <div style={{
          position: 'absolute',
          top: 20,
          left: '50%',
          transform: 'translate(-50%, 0%)',
          zIndex: 666,
        }}>
          <ThemeProvider theme={darkMode ? darkTheme : theme}>
            <FormControl component="fieldset" >
              <ToggleButtonGroup
                value={displayType}
                exclusive
                onChange={(_, newVal) => newVal && setDisplayType(newVal)}
                size="small"
                style={{ height: 28, backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'] }}
              >
                <ToggleButton value="2d" size="small" color={(darkMode ? "" : "primary")} title="2D View">
                  2D
                </ToggleButton>
                <ToggleButton value="3d" size="small" color={(darkMode ? "" : "primary")} title="3D View">
                  3D (Beta)
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          </ThemeProvider>

          <NewLabel style={{ position: 'absolute', top: -15, right: -40 }} />
        </div>
        {
          displayType === "2d" ?
            <MapContainer
              center={[globalCenter?.lat, globalCenter?.lon]}
              zoom={14}
              maxZoom={16}
              ref={mapRef}
              style={{ width: '100%', height: '100%', position: 'relative', backgroundColor: darkMode ? 'black' : 'white' }}
              attributionControl={true}
              zoomControl={false}
              scrollWheelZoom={false}
            >

              {
                darkMode ?
                  <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`} /> :
                  <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`} />
              }

              {Object.keys(wellCoordinates)?.map((well, index) => {
                return (
                  <div key={`${well}-${index}`}>
                    <Polyline 
                      pathOptions={{ color: well === currentWell ? 'tomato' : currentWellName === 'All' ? 'black' : 'lightgrey', weight: 6 }} 
                      positions={wellCoordinates[well]?.map(w => [w.lat, w.lon])} 
                      bounds={L.latLngBounds(wellCoordinates[well]?.map(w => [w.lat, w.lon]))} 
                    />
                    <CircleMarker center={[currentRTFO?.surveys?.wells?.[well]?.surfaceLat, currentRTFO?.surveys?.wells?.[well]?.surfaceLon]} pathOptions={{ color: (currentWellName === 'All' || well === currentWell) ? 'black' : 'lightgrey' }} radius={10}>
                    </CircleMarker>
                    <WellLabel 
                      center={[wellCoordinates[well]?.[wellCoordinates[well]?.length - 1]?.lon, wellCoordinates[well]?.[wellCoordinates[well]?.length - 1]?.lat]} 
                      wellName={well} 
                      currentWellName={currentStageName?.split('_')?.[1]?.toLowerCase()} 
                      darkMode={darkMode}
                    />
                  </div>
                );
              })}

              {monitorWellCoordinates && Object.keys(monitorWellCoordinates)?.map((well, index) => {
                try {
                  let currentMonitor = false;
                  if (currentRTFO?.client === 'marathon') {
                    currentMonitor = well === formattedMonitorUWI;
                  } else {
                    currentMonitor = stripMonitorUWI(well) === stripMonitorUWI(formattedMonitorUWI);
                  };
                  return (
                    (Math.abs(monitorWellCoordinates[well]?.[0]?.lat) > 0 && Math.abs(monitorWellCoordinates[well]?.[0]?.lon) > 0) ?
                      <div key={`${well}-${index}`}>
                        <Polyline
                          pathOptions={{ color: currentMonitor ? 'tomato' : 'lightgrey', weight: 6 }}
                          positions={monitorWellCoordinates[well]?.map(w => [w.lat, w.lon])}
                          bounds={L.latLngBounds(monitorWellCoordinates[well]?.map(w => [w.lat, w.lon]))}
                          eventHandlers={{
                            click: () => {
                              // let formattedMonitorUWI = well;
                              // if (formattedMonitorUWI?.includes('/')) {
                              //   formattedMonitorUWI = formattedMonitorUWI?.split('/');
                              //   formattedMonitorUWI = `${formattedMonitorUWI?.[0]}-${formattedMonitorUWI?.[1]?.split('-')?.slice(0, 2)?.join('-')}`;
                              // };
                              // setMonitorUWI(formattedMonitorUWI);
                              setMonitorUWI(well);
                            }
                          }}
                        />
                        <CircleMarker center={[currentRTFO?.monitorSurveys?.[well]?.surfaceLat, currentRTFO?.monitorSurveys?.[well]?.surfaceLon]} pathOptions={{ color: currentMonitor ? 'tomato' : 'lightgrey' }} radius={10}>
                        </CircleMarker>
                        {!isNaN(monitorWellCoordinates[well]?.[monitorWellCoordinates[well]?.length - 1]?.lon) && !isNaN(monitorWellCoordinates[well]?.[monitorWellCoordinates[well]?.length - 1]?.lat) ?
                          <WellLabel 
                            center={[monitorWellCoordinates[well]?.[monitorWellCoordinates[well]?.length - 1]?.lon, monitorWellCoordinates[well]?.[monitorWellCoordinates[well]?.length - 1]?.lat]} 
                            wellName={well} 
                            currentWellName={formattedMonitorUWI} 
                            darkMode={darkMode}
                          /> : <div key={`${well}-${index}`}></div>}
                      </div> : <div key={`${well}-${index}`}></div>
                  );
                } catch (error) {
                  console.log('Could not plot monitor well');
                  return <div key={`${well}-${index}`}></div>;
                };
              })}

              {
                allInterferenceLineCoordinates && Object.keys(allInterferenceLineCoordinates)?.map(monitorName => {
                  try {
                    return Object.keys(allInterferenceLineCoordinates?.[monitorName])?.map(stageName => {
                      return (
                        Math.abs(allInterferenceLineCoordinates?.[monitorName]?.[stageName]?.[0]?.[0]) > 0 && Math.abs(allInterferenceLineCoordinates?.[monitorName]?.[stageName]?.[0]?.[1]) > 0 && Math.abs(allInterferenceLineCoordinates?.[monitorName]?.[stageName]?.[1]?.[0]) > 0 && Math.abs(allInterferenceLineCoordinates?.[monitorName]?.[stageName]?.[1]?.[1]) > 0 ?
                          <PolylineDecorator patterns={arrow} polyline={allInterferenceLineCoordinates?.[monitorName]?.[stageName]} bounds={L.latLngBounds(allInterferenceLineCoordinates?.[monitorName]?.[stageName])} key={monitorName + '-' + stageName} /> :
                          <div key={monitorName + '-' + stageName}></div>
                      );
                    });
                  } catch (error) {
                    console.log('Could not plot interference line');
                    return <div key={monitorName}></div>;
                  };
                })
              }

              {stagePolygonsCoordinates && Object.keys(stagePolygonsCoordinates)?.map(stageName => {
                return (
                  <Polygon 
                    positions={stagePolygonsCoordinates?.[stageName]} 
                    pathOptions={{ fillColor: stagesWithEvents?.includes(stageName) ? 'blue' : getClusterLinesColor(stageName), fillOpacity: 0.75, color: 'transparent' }}
                    eventHandlers={{
                      click: () => {
                        if (currentRTFO?.stages?.includes(stageName)) {
                          if (!currentRTFO?.stageData?.[stageName]?.highResolutionData) {
                            dispatch(setIsLoadingData(true));
                            // socket?.send(JSON.stringify({
                            //   messageType: 'getStageData',
                            //   stageName
                            // }));
                          };
                          dispatch(setCurrentStageName(stageName));
                        };
                      }
                    }}
                    key={stageName} 
                  />
                );
              })}

              {Object.keys(stageCoordinates3D)?.map(stageName => (
                Object.keys(clusterCoordinates3D[stageName])?.map((cluster, cIndex) => {
                  const clusterLineCoordinates = [
                    [clusterCoordinates3D[stageName]?.[cluster]?.center?.start?.lat, clusterCoordinates3D[stageName]?.[cluster]?.center?.start?.lon],
                    [clusterCoordinates3D[stageName]?.[cluster]?.center?.center?.lat, clusterCoordinates3D[stageName]?.[cluster]?.center?.center?.lon],
                    [clusterCoordinates3D[stageName]?.[cluster]?.center?.end?.lat, clusterCoordinates3D[stageName]?.[cluster]?.center?.end?.lon]
                  ];
                  return (
                    Math.abs(clusterLineCoordinates?.[0]?.[0]) > 0 && Math.abs(clusterLineCoordinates?.[1]?.[0]) > 0 && Math.abs(clusterLineCoordinates?.[2]?.[0]) > 0 ?
                      <Polyline 
                        pathOptions={{ weight: stageName === currentStageName ? 2 : 1, color: getClusterLinesColor(stageName), lineCap: 'butt' }} 
                        positions={clusterLineCoordinates} 
                        bounds={L.latLngBounds(clusterLineCoordinates)} 
                        eventHandlers={{
                          click: () => {
                            if (currentRTFO?.stages?.includes(stageName)) {
                              if (!currentRTFO?.stageData?.[stageName]?.highResolutionData) {
                                dispatch(setIsLoadingData(true));
                                // socket?.send(JSON.stringify({
                                //   messageType: 'getStageData',
                                //   stageName
                                // }));
                              };
                              dispatch(setCurrentStageName(stageName));
                            };
                          }
                        }}
                        key={`${stageName}-${cluster}-${cIndex}`}
                      >
                      </Polyline>
                      :
                      <div key={cIndex}></div>
                  );
                })
              ))}

              {fiberEventsCoordinates && Object.keys(fiberEventsCoordinates)?.map(eventId => {
                const eventData = fiberData.find(event => +event.eventId === +eventId)
                const eventTypeId = eventData?.eventTypeId;
                const eventColor = eventTypeId === 0 ? 'purple' : eventTypeId === 1 ? 'blue' : eventTypeId === 2 ? 'tomato' : eventTypeId === 3 ? 'orange' : 'grey';
                return (
                  <Polygon positions={fiberEventsCoordinates?.[eventId]?.wide} pathOptions={{ fillColor: eventColor, fillOpacity: 0.3, color: 'transparent' }} key={eventId}>
                    <Tooltip className={darkMode ? "map-tooltip-dark" : "map-tooltip"} direction={"right"} offset={[10, 0]} opacity={1}>
                      <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'], color: darkMode ? 'white' : 'black', padding: 4 }}>
                        <Typography variant="caption" fontWeight="bold">Frac-Driven Interaction</Typography>
                        <Typography variant="caption">ID: {eventData?.eventId}</Typography>
                        <Typography variant="caption">Well: {eventData?.wellName}</Typography>
                        <Typography variant="caption">Stage: {eventData?.stageNumber}</Typography>
                        <Typography variant="caption">MD ({units === 'field' ? 'ft' : 'm'}): {eventData?.centerDepth * (units === 'field' ? rtfoConversionFactors.length : 1)}</Typography>
                        <Typography variant="caption">Distance ({units === 'field' ? 'ft' : 'm'}): {(eventData?.distance * (units === 'field' ? rtfoConversionFactors.length : 1))?.toFixed(1)}</Typography>
                        <Typography variant="caption">Type: {eventData?.eventType}</Typography>
                      </div>
                    </Tooltip>
                  </Polygon>
                )
              })}
              {fiberEventsCoordinates && Object.keys(fiberEventsCoordinates)?.map(eventId => {
                const eventData = fiberData.find(event => +event.eventId === +eventId)
                const eventTypeId = eventData?.eventTypeId;
                const eventColor = eventTypeId === 0 ? 'purple' : eventTypeId === 1 ? 'blue' : eventTypeId === 2 ? 'tomato' : eventTypeId === 3 ? 'orange' : 'grey';
                return (
                  <Polygon positions={fiberEventsCoordinates?.[eventId]?.wide} pathOptions={{ fillColor: eventColor, fillOpacity: 0.3, color: 'transparent' }} key={eventId}>
                    <Tooltip className={darkMode ? "map-tooltip-dark" : "map-tooltip"} direction={"right"} offset={[10, 0]} opacity={1}>
                      <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'], color: darkMode ? "white" : "black", padding: 4 }}>
                        <Typography variant="caption" fontWeight="bold">Frac-Driven Interaction</Typography>
                        <Typography variant="caption">ID: {eventData?.eventId}</Typography>
                        <Typography variant="caption">Well: {eventData?.wellName}</Typography>
                        <Typography variant="caption">Stage: {eventData?.stageNumber}</Typography>
                        <Typography variant="caption">MD ({units === 'field' ? 'ft' : 'm'}): {eventData?.centerDepth * (units === 'field' ? rtfoConversionFactors.length : 1)}</Typography>
                        <Typography variant="caption">Distance ({units === 'field' ? 'ft' : 'm'}): {(eventData?.distance * (units === 'field' ? rtfoConversionFactors.length : 1))?.toFixed(1)}</Typography>
                        <Typography variant="caption">Type: {eventData?.eventType}</Typography>
                      </div>
                    </Tooltip>
                  </Polygon>
                )
              })}

              {allMonitorWellsIntersectionCoordinates && Object.keys(allMonitorWellsIntersectionCoordinates)?.map(monitorName => {
                try {
                  return Object.keys(allMonitorWellsIntersectionCoordinates?.[monitorName])?.map(stageName => {
                    return (
                      Math.abs(allMonitorWellsIntersectionCoordinates?.[monitorName]?.[stageName]?.[0]) > 0 && Math.abs(allMonitorWellsIntersectionCoordinates?.[monitorName]?.[stageName]?.[1]) > 0 ?
                        <CircleMarker center={allMonitorWellsIntersectionCoordinates?.[monitorName]?.[stageName]} pathOptions={{ color: 'tomato', weight: 2 }} radius={5} key={monitorName + '-' + stageName}>
                          <Tooltip className={darkMode ? "map-tooltip-dark" : "map-tooltip"} direction={"right"} offset={[10, 0]} opacity={1}>
                            <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'], color: darkMode ? "white" : "black", padding: 4 }}>
                              <Typography variant="caption" fontWeight="bold">Frac-Driven Interaction</Typography>
                              <Typography variant="caption">Well: {stageName?.split('_')?.[1]}</Typography>
                              <Typography variant="caption">Stage: {stageName?.split('_')?.[2]}</Typography>
                              <Typography variant="caption">MD ({units === 'field' ? 'ft' : 'm'}): {(allMonitorWellsIntersectionMD?.[monitorName]?.[stageName] || 0) * (units === 'field' ? rtfoConversionFactors.length : 1)}</Typography>
                              <Typography variant="caption">Distance ({units === 'field' ? 'ft' : 'm'}): {(allInterferenceLineDistances?.[monitorName]?.[stageName] * (units === 'field' ? rtfoConversionFactors.length : 1))?.toFixed(1)}</Typography>
                              <Typography variant="caption">Events: {Math.max((monitorWellEvents?.filter(e => e?.monitorUWI === monitorName && e?.stageName === stageName)?.length - 1), 0)}</Typography>
                            </div>
                          </Tooltip>
                        </CircleMarker> : <div key={monitorName + '-' + stageName}></div>
                    )
                  })
                } catch (error) {
                  console.log('Could not plot intersection point');
                  return <div key={monitorName}></div>
                };
              })}
              {allMonitorWellsIntersectionCoordinates && Object.keys(allMonitorWellsIntersectionCoordinates)?.map(monitorName => {
                try {
                  return Object.keys(allMonitorWellsIntersectionCoordinates?.[monitorName])?.map(stageName => {
                    return (
                      Math.abs(allMonitorWellsIntersectionCoordinates?.[monitorName]?.[stageName]?.[0]) > 0 && Math.abs(allMonitorWellsIntersectionCoordinates?.[monitorName]?.[stageName]?.[1]) > 0 ?
                        <CircleMarker center={allMonitorWellsIntersectionCoordinates?.[monitorName]?.[stageName]} pathOptions={{ color: 'tomato', weight: 2 }} radius={5} key={monitorName + '-' + stageName}>
                          <Tooltip className={darkMode ? "map-tooltip-dark" : "map-tooltip"} direction={"right"} offset={[10, 0]} opacity={1}>
                            <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'], padding: 4, color: darkMode ? "white" : "black" }}>
                              <Typography variant="caption" fontWeight="bold">Frac-Driven Interaction</Typography>
                              <Typography variant="caption">Well: {stageName?.split('_')?.[1]}</Typography>
                              <Typography variant="caption">Stage: {stageName?.split('_')?.[2]}</Typography>
                              <Typography variant="caption">MD ({units === 'field' ? 'ft' : 'm'}): {(allMonitorWellsIntersectionMD?.[monitorName]?.[stageName] || 0) * (units === 'field' ? rtfoConversionFactors.length : 1)}</Typography>
                              <Typography variant="caption">Distance ({units === 'field' ? 'ft' : 'm'}): {(allInterferenceLineDistances?.[monitorName]?.[stageName] * (units === 'field' ? rtfoConversionFactors.length : 1))?.toFixed(1)}</Typography>
                              <Typography variant="caption">Events: {Math.max((monitorWellEvents?.filter(e => e?.monitorUWI === monitorName && e?.stageName === stageName)?.length - 1), 0)}</Typography>
                            </div>
                          </Tooltip>
                        </CircleMarker> : <div key={monitorName + '-' + stageName}></div>
                    )
                  })
                } catch (error) {
                  console.log('Could not plot intersection point');
                  return <div key={monitorName}></div>
                };
              })}

              {allStages?.map(stageName => {
                return (
                  stageCoordinates3D[stageName] ?
                    <StageLabel 
                      key={stageName} 
                      coord={stageCoordinates3D[stageName]?.center} 
                      stageName={stageName} 
                      darkMode={darkMode}
                    /> : <div key={stageName}></div>
                );
              })}
              {allStages?.map(stageName => {
                return (
                  stageCoordinates3D[stageName] ?
                    <StageLabel 
                      key={stageName} 
                      coord={stageCoordinates3D[stageName]?.center} 
                      stageName={stageName} 
                      darkMode={darkMode}
                    /> : <div key={stageName}></div>
                );
              })}

              {fiberData?.length > 0 && <RTFOPressureAnalysisMapLegend />}
              {fiberData?.length > 0 && <RTFOPressureAnalysisMapLegend />}

              <ScrollZoomControl setScrollZoomControlOverlayVisible={setScrollZoomControlOverlayVisible} darkMode={darkMode} />

              {scrollZoomControlOverlayVisible && <ScrollZoomControlOverlay setScrollZoomControlOverlayVisible={setScrollZoomControlOverlayVisible} darkMode={darkMode} />}
            </MapContainer>
            :
            <RTFOWellbore3D
              isFDI={true}
              width={"100%"}
              height={gridView ? 540 : 900}
              allInterferenceLineCoordinates={allInterferenceLineCoordinates}
              darkMode={darkMode}
            />
        }

      </Paper>
    );

    function getClusterLinesColor(stageName) {
      if (currentRTFO?.currentStageName === stageName) {
        return 'tomato';
      } else if (currentRTFO?.stages?.includes(stageName)) {
        return darkMode ? 'lightgrey' : 'grey';
      } else {
        return darkMode ? 'black' : 'lightgrey';
      };
    };

    function StageLabel({ coord, stageName, darkMode }) {
      try {
        const center = [coord?.center?.lon, coord?.center?.lat];
        const radius = 60 / 1000;
        const options = { steps: 16, units: 'kilometers' };
        const circle = turf.circle(center, radius, options);
        let coords = circle?.geometry?.['coordinates']?.[0]?.map(c => [c?.[1], c?.[0]]);

        const width = 90;
        const height = 140;

        // Calculate x and y coordinates of the rectangle center
        const centerX = center?.[1];
        const centerY = center?.[0];

        // Calculate x and y coordinates of the top-left corner of the rectangle bounding box
        const x = centerX - width / 2;
        const y = centerY - height / 2;

        return (
          <SVGOverlay
            bounds={L.latLngBounds(coords)}
            attributes={{
              viewBox: `${x} ${y} ${width} ${height}`
            }}
          >
            <text 
              x={centerX} 
              y={centerY} 
              textAnchor="middle" 
              dominantBaseline="middle" 
              strokeWidth="2px" 
              paintOrder="stroke" 
              stroke={darkMode ? "black" : "white"}
              fill={darkMode ? "white" : "black"}
              fontSize={'32px'} 
              fontWeight="bold"
            >
              {stageName?.split('_')?.[2]}
            </text>
          </SVGOverlay>
        );

      } catch (error) {
        return <></>
      };

    };
  } catch (error) {
    console.log(error);
    return <></>;
  };
};

function PolylineDecorator({ patterns, polyline }) {
  const map = useMap();
  const polylineRef = useRef();
  const decoratorRef = useRef();

  useEffect(() => {
    if (!map) return;

    // Remove previous polyline and decorator if they exist
    if (polylineRef.current) {
      polylineRef.current.removeFrom(map);
    };
    if (decoratorRef.current) {
      decoratorRef.current.removeFrom(map);
    };

    // Add new polyline
    const newPolyline = L.polyline(polyline, { color: 'tomato' }).addTo(map);
    polylineRef.current = newPolyline;

    // Add decorator to the new polyline
    const newDecorator = L.polylineDecorator(newPolyline, {
      patterns
    }).addTo(map);
    decoratorRef.current = newDecorator;

    // Cleanup on component unmount
    return () => {
      if (polylineRef.current) {
        polylineRef.current.removeFrom(map);
      };
      if (decoratorRef.current) {
        decoratorRef.current.removeFrom(map);
      };
    };
  }, [map, patterns, polyline]);

  return null;
};



export function surveyToCoordinates(surfaceLat, surfaceLon, surveyData) {
  try {
    let coordinates = [];

    // Initialize variables for minimum curvature algorithm
    let x0 = surfaceLat;
    let y0 = surfaceLon;
    let z0 = 0;
    let mdPrev = 0;

    // Iterate through survey points to calculate wellbore path
    for (let i = 0; i < surveyData.length; i++) {
      let md = surveyData[i].measuredDepth;
      let dip = surveyData[i].deviation;
      let azi = surveyData[i].direction;
      let dMD = md - mdPrev;
      let radians = dip * Math.PI / 180;
      let aziRad = azi * Math.PI / 180;

      // Calculate new coordinates with minimum curvature algorithm
      let dx = dMD * Math.sin(radians) * Math.cos(aziRad);
      let dy = dMD * Math.sin(radians) * Math.sin(aziRad);
      let dz = dMD * Math.cos(radians);
      let x = x0 + dx / 111111;
      let y = y0 + dy / (111111 * Math.cos(surfaceLat * Math.PI / 180));
      let z = z0 + dz;
      let lat = x;
      let lon = y;

      // Add the lat, lon coordinates to the array
      coordinates.push({ lat, lon, tvd: surveyData[i].trueVerticalDepth });

      // Update variables for next iteration
      x0 = x;
      y0 = y;
      z0 = z;
      mdPrev = md;

    };

    return coordinates;

  } catch (error) {
    console.log(error);
    return [];
  };
};

export function interpolateCoordinates(surveyData, coordinates, desiredMd) {
  try {
    // find index of the surveyData that is closest to the desiredMd
    let index = surveyData?.findIndex((data) => data.measuredDepth >= desiredMd);

    if (!index) return [0, 0];

    if (!index > 0 || index === -1) {
      index = surveyData.length - 1;
    };
    const prevIndex = index - 1;

    // interpolate coordinates [lat, lon] between index and prevIndex
    const md1 = surveyData[prevIndex].measuredDepth;
    const md2 = surveyData[index].measuredDepth;
    const lat1 = coordinates[prevIndex][0];
    const lat2 = coordinates[index][0];
    const lon1 = coordinates[prevIndex][1];
    const lon2 = coordinates[index][1];
    const lat = ((desiredMd - md1) / (md2 - md1)) * (lat2 - lat1) + lat1;
    const lon = ((desiredMd - md1) / (md2 - md1)) * (lon2 - lon1) + lon1;

    return [lat, lon];

  } catch (error) {
    console.log(error);
    return [0, 0];
  };

};

export function calculateLineCoordinates(center, length) {
  try {
    // Define the center point and length in meters
    const centerPoint = turf.point([center?.[1], center[0]]); // center point

    // Calculate the start and end points using Turf.js
    const bearing = 90; // Angle in degrees (e.g., 90 for an eastward line)
    const start = turf.destination(centerPoint, length / 2, bearing, { units: 'meters' });
    const end = turf.destination(centerPoint, length / 2, bearing - 180, { units: 'meters' });

    // Extract the coordinates of the start and end points
    const startCoordinates = start.geometry.coordinates;
    const endCoordinates = end.geometry.coordinates;

    return [[startCoordinates?.[1], startCoordinates?.[0]], center, [endCoordinates?.[1], endCoordinates?.[0]]];
  } catch (error) {
    console.log('Calculate line coordinates error');
    return null;
  };
};

export function rotatePolyline(originalLine, angle) {
  try {
    const start = [originalLine?.[0]?.[1], originalLine?.[0]?.[0]];
    const center = [originalLine?.[1]?.[1], originalLine?.[1]?.[0]];
    const end = [originalLine?.[2]?.[1], originalLine?.[2]?.[0]];

    const polyline = turf.lineString([start, center, end]);
    const options = { pivot: center };
    const rotatedPolyline = turf.transformRotate(polyline, -(90 - angle), options);

    const rotatedCoordinates = turf.getCoords(rotatedPolyline);

    return [[rotatedCoordinates?.[0]?.[1], rotatedCoordinates?.[0]?.[0]], [rotatedCoordinates?.[1]?.[1], rotatedCoordinates?.[1]?.[0]], [rotatedCoordinates?.[2]?.[1], rotatedCoordinates?.[2]?.[0]]];

  } catch (error) {
    console.log('Rotate line coordinates error');
    return null;
  };
};

export function findTVD(surveyData, md) {
  try {

    const closestPoint = surveyData?.find((data) => data.measuredDepth >= md);
    return closestPoint?.trueVerticalDepth || 0;

  } catch (error) {
    console.log(error);
  };
};

// Function to calculate distance between two points on a sphere

export function calculateDistance(start, end) {
  try {
    if (!start || !end || start.length !== 2 || end.length !== 2) return 0;
    const R = 6371e3; // Earth's radius in meters
    const phi1 = (start[0] * Math.PI) / 180; // Convert latitude 1 to radians
    const phi2 = (end[0] * Math.PI) / 180; // Convert latitude 2 to radians
    const deltaPhi = ((end[0] - start[0]) * Math.PI) / 180; // Difference in latitudes
    const deltaLambda = ((end[1] - start[1]) * Math.PI) / 180; // Difference in longitudes
    const a =
      Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
      Math.cos(phi1) *
      Math.cos(phi2) *
      Math.sin(deltaLambda / 2) *
      Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)); // Central angle
    const distance = R * c; // Distance between the two points
    return distance;

  } catch (error) {
    console.log(error);
    return 0;
  };
};

export function calculateSignedDistance(start, end) {
  try {
    if (!start || !end || start.length !== 2 || end.length !== 2) return 0;
    const R = 6371e3; // Earth's radius in meters
    const phi1 = (start[0] * Math.PI) / 180; // Convert latitude 1 to radians
    const phi2 = (end[0] * Math.PI) / 180; // Convert latitude 2 to radians
    const deltaPhi = ((end[0] - start[0]) * Math.PI) / 180; // Difference in latitudes
    const deltaLambda = ((end[1] - start[1]) * Math.PI) / 180; // Difference in longitudes
    const a =
      Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
      Math.cos(phi1) *
      Math.cos(phi2) *
      Math.sin(deltaLambda / 2) *
      Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)); // Central angle
    const distance = R * c; // Distance between the two points

    // Calculate the initial bearing from start to end
    const y = Math.sin(deltaLambda) * Math.cos(phi2);
    const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);
    const initialBearing = Math.atan2(y, x);

    // Calculate the signed distance based on the initial bearing
    const signedDistance = Math.sin(initialBearing) * distance;

    return signedDistance;

  } catch (error) {
    console.log(error);
    return 0;
  };
};

export function calculateDistance3D(point1, point2) {
  const horizontalDistance = haversineDistance(point1, point2);
  const verticalDistance = Math.abs(point2.tvd - point1.tvd);
  const distance3D = Math.sqrt(horizontalDistance ** 2 + verticalDistance ** 2);
  return distance3D;

  function haversineDistance(point1, point2) {
    const R = 6371000; // Earth's radius in meters

    const lat1 = (Math.PI / 180) * point1.lat;
    const lon1 = (Math.PI / 180) * point1.lon;
    const lat2 = (Math.PI / 180) * point2.lat;
    const lon2 = (Math.PI / 180) * point2.lon;

    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };
};

export function getMiddlePoint(lines) {
  try {
    let totalLat = 0;
    let totalLon = 0;
    let totalPoints = 0;

    for (const line of lines) {
      let lineLat = 0;
      let lineLon = 0;
      const numPoints = line?.length;

      for (const point of line) {
        lineLat += point[0];
        lineLon += point[1];
      };

      totalLat += lineLat / numPoints;
      totalLon += lineLon / numPoints;
      totalPoints++;
    };

    const avgLat = totalLat / totalPoints;
    const avgLon = totalLon / totalPoints;

    return [avgLat, avgLon];

  } catch (error) {
    console.log(error);
    return [0, 0];
  };
};

export function findIntersectionCoordinates(stageLineCoordinates, monitorWellCoordinates, monitorSurvey) {
  try {
    if (!stageLineCoordinates?.length > 0 || !monitorWellCoordinates?.length > 0) return null;

    let horizontalCoordinates = [...monitorWellCoordinates?.map(d => [d.lon || 0, d.lat || 0])];

    if (monitorSurvey) {
      const startHorizontalIndex = monitorSurvey?.findIndex((d, i) => monitorSurvey?.[i]?.deviation >= 80);
      const startHorizontalDepth = monitorSurvey?.[startHorizontalIndex]?.measuredDepth;

      const first500Index = monitorSurvey?.findIndex((d, i) => i > startHorizontalIndex && monitorSurvey?.[i]?.measuredDepth >= startHorizontalDepth + 500);
      const last500Index = monitorSurvey?.findIndex((d, i) => i > startHorizontalIndex && monitorSurvey?.[i]?.measuredDepth >= monitorSurvey?.slice(-1)?.[0]?.measuredDepth - 1000);

      // extrapolate monitor well coordinates
      horizontalCoordinates = extrapolateMonitorWellTrajectory(horizontalCoordinates, startHorizontalIndex, first500Index, last500Index);

    };

    const line1 = turf.lineString([[stageLineCoordinates?.[0]?.[1], stageLineCoordinates?.[0]?.[0]], [stageLineCoordinates?.[2]?.[1], stageLineCoordinates?.[2]?.[0]]]); // long, lat format
    const line2 = turf.lineString(horizontalCoordinates); // long, lat format
    const intersects = turf.lineIntersect(line1, line2);

    if (!intersects?.features?.length > 0) return null;

    return [intersects?.features?.[0]?.geometry?.coordinates?.[1] || null, intersects?.features?.[0]?.geometry?.coordinates?.[0] || null];

  } catch (error) {
    console.log(error);
    return null;
  };
};

function extrapolateMonitorWellTrajectory(monitorWellCoordinates, startHorizontalIndex, first500Index, last500Index) {
  try {

    const first500PointStart = monitorWellCoordinates?.[startHorizontalIndex];
    const first500PointEnd = monitorWellCoordinates?.[first500Index];

    const startBearing = getBearing(first500PointStart, first500PointEnd);
    const newBackwardPoint = extrapolatePoint(first500PointStart, getOppositeBearing(startBearing), 5); // extrapolate 5 km backwards

    const last500PointStart = monitorWellCoordinates?.[last500Index];
    const last500PointEnd = monitorWellCoordinates?.slice(-1)?.[0];

    const lastBearing = getBearing(last500PointStart, last500PointEnd);

    const newForwardPoint = extrapolatePoint(monitorWellCoordinates?.slice(-1)?.[0], lastBearing, 5); // extrapolate 5 km forward

    const newMonitorWellCoordinates = monitorWellCoordinates.slice(startHorizontalIndex,);

    return [newBackwardPoint?.geometry?.coordinates, ...newMonitorWellCoordinates, newForwardPoint?.geometry?.coordinates];

  } catch (error) {
    console.log(error);
    return monitorWellCoordinates;
  };

  function getBearing(start, end) {
    return turf.bearing(start, end);
  };

  // Function to extrapolate a point
  function extrapolatePoint(point, bearing, distance) {
    return turf.destination(point, distance, bearing);
  };

  function getOppositeBearing(bearing) {
    if (bearing >= 0) {
      return (bearing - 180) % 360;
    } else {
      return (bearing + 180) % 360;
    };
  };
};

export function findIntersectionCoordinates2(stageLine, wellName, wellCoordinates, surveyData) {
  try {

    // Stage line coordinates (latitude and longitude)
    const lat1 = stageLine?.[0]?.[0];
    const lon1 = stageLine?.[0]?.[1];
    const lat2 = stageLine?.[2]?.[0];
    const lon2 = stageLine?.[2]?.[1];

    // Find the horizontal portion of the wellbore
    const horizontalIndex = surveyData?.findIndex(s => s.deviation >= 75);

    if (!horizontalIndex > 0) return null;

    const horizontalWellCoordinates = wellCoordinates[wellName]?.slice(horizontalIndex,);

    // Wellbore line coordinates (latitude and longitude) - a line from first stage to the last stage
    const lat3 = horizontalWellCoordinates?.[0]?.lat;
    const lon3 = horizontalWellCoordinates?.[0]?.lon;
    const lat4 = horizontalWellCoordinates?.[horizontalWellCoordinates?.length - 1]?.lat;
    const lon4 = horizontalWellCoordinates?.[horizontalWellCoordinates?.length - 1]?.lon;

    // Convert latitude and longitude to radians
    const lat1Rad = (lat1 * Math.PI) / 180;
    const lon1Rad = (lon1 * Math.PI) / 180;
    const lat2Rad = (lat2 * Math.PI) / 180;
    const lon2Rad = (lon2 * Math.PI) / 180;
    const lat3Rad = (lat3 * Math.PI) / 180;
    const lon3Rad = (lon3 * Math.PI) / 180;
    const lat4Rad = (lat4 * Math.PI) / 180;
    const lon4Rad = (lon4 * Math.PI) / 180;

    // Calculate the direction vectors
    const dLon1 = lon2Rad - lon1Rad;
    const dLat1 = lat2Rad - lat1Rad;
    const dLon2 = lon4Rad - lon3Rad;
    const dLat2 = lat4Rad - lat3Rad;

    // Calculate the determinant
    const det = dLon1 * dLat2 - dLon2 * dLat1;

    // Check if the lines are parallel (det == 0)
    if (det === 0) {
      return null; // No intersection
    };

    // Calculate the parameters for the point of intersection
    const t1 = ((lon3Rad - lon1Rad) * dLat2 - (lat3Rad - lat1Rad) * dLon2) / det;
    const t2 = ((lon3Rad - lon1Rad) * dLat1 - (lat3Rad - lat1Rad) * dLon1) / det;

    // Check if the intersection point is within the line segments
    if (t1 >= 0 && t1 <= 1 && t2 >= 0 && t2 <= 1) {
      const latIntersection = lat1Rad + t1 * dLat1;
      const lonIntersection = lon1Rad + t1 * dLon1;
      // Convert back to degrees
      const intersection = [latIntersection * (180 / Math.PI), lonIntersection * (180 / Math.PI)];
      return intersection;
    } else {
      return null; // Intersection is outside the line segments
    };
  } catch (error) {
    console.log(error);
    return null;
  };
};

export function findIntersectionMD(intersectionCoordinates, monitorWellCoordinates, monitorWellSurveyData) {
  try {
    let minDistance = Number.MAX_VALUE;
    let intersectionMD = 0;

    for (let i = 0; i < monitorWellCoordinates?.length; i++) {
      const coord = monitorWellCoordinates?.[i];
      const distance = calculateDistance(intersectionCoordinates, [coord?.lat, coord?.lon]);
      if (distance < minDistance) {
        minDistance = distance;
        intersectionMD = monitorWellSurveyData?.[i]?.measuredDepth;
      };
    };

    return intersectionMD;
  } catch (error) {
    console.log(error);
    return null;
  };
};

export function calculateCenterPoint(points) {
  try {
    if (points.length < 2) {
      return { lat: points?.[0]?.lat, lon: points?.[0]?.lon, tvd: points?.[0]?.tvd }; // You need at least two points to calculate a midpoint
    };

    // Convert latitude and longitude from degrees to radians
    const toRadians = (angle) => (angle * Math.PI) / 180;
    const toDegrees = (radians) => (radians * 180) / Math.PI;

    const filteredPoints = points.filter(p => p.lat !== 0 && typeof p.lat === 'number' && !isNaN(p.lat) && p.lon !== 0 && typeof p.lon === 'number' && !isNaN(p.lon));
    const sumLatitudes = filteredPoints?.reduce((sum, point) => sum + toRadians(point.lat), 0);
    const sumLongitudes = filteredPoints?.reduce((sum, point) => sum + toRadians(point.lon), 0);
    const sumTVD = filteredPoints.reduce((sum, point) => sum + point.tvd, 0);

    const averageLatitude = sumLatitudes / filteredPoints.length;
    const averageLongitude = sumLongitudes / filteredPoints.length;
    const averageTVD = sumTVD / filteredPoints.length;

    const midpointLatitude = toDegrees(averageLatitude);
    const midpointLongitude = toDegrees(averageLongitude);

    return { lat: midpointLatitude, lon: midpointLongitude, tvd: averageTVD };

  } catch (error) {
    console.log(error);
    return { lat: 0, lon: 0, tvd: 0 };
  };
};

export function findClosestPointToIntersection(wellName, intersection, wellCoordinates) {
  try {
    if (!intersection) return { lat: 0, lon: 0, tvd: 0, name: wellName };

    let closestPointCoordinates = null;
    let closestDistance = Infinity;

    for (const point of wellCoordinates[wellName]) {
      const lat = point.lat;
      const lon = point.lon;

      const distance = Math.sqrt(
        Math.pow(lat - intersection[0], 2) +
        Math.pow(lon - intersection[1], 2)
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestPointCoordinates = { lat, lon, tvd: point.tvd, name: wellName };
      };
    };

    return closestPointCoordinates;

  } catch (error) {
    console.log(error);
    return { lat: 0, lon: 0, tvd: 0, name: wellName };
  };
};

export function findClosestStageToIntersection(wellName, intersection, clusterCoordinates, currentStageName, stageData, shadowRadius) {
  try {
    if (!intersection) return '';

    let closestStageName = null;
    let closestDistance = Infinity;
    const distanceThreshold = shadowRadius || 300;

    const currentStageStartTimestamp = stageData?.[currentStageName]?.coldboreData?.[0]?.timestamp;

    for (const stageName in clusterCoordinates) {
      if (stageName?.split('_')?.[1]?.toLowerCase() !== wellName?.toLowerCase() || stageData?.[stageName]?.coldboreData?.[0]?.timestamp > currentStageStartTimestamp) continue;

      for (const clusterNumber in clusterCoordinates[stageName]) {
        const point = clusterCoordinates[stageName][clusterNumber].center.center; // Extract lat and lon
        const lat = point.lat;
        const lon = point.lon;

        const distance = calculateDistance([lat, lon], intersection);

        if (distance < distanceThreshold && distance < closestDistance) {
          closestDistance = distance;
          closestStageName = stageName;
        };
      };
    };

    return closestStageName;

  } catch (error) {
    console.log(error);
    return '';
  };
};

export function rgbToRgba(rgb, alpha) {
  try {
    if (!rgb) return '';

    const rgbValues = rgb.match(/\d+/g); // Extract RGB values using regular expression

    if (!rgbValues || rgbValues.length !== 3) {
      throw new Error('Invalid RGB color format');
    };

    const [r, g, b] = rgbValues.map(Number);

    // Ensure the alpha value is between 0 and 1
    alpha = Math.min(1, Math.max(0, alpha));

    // Create the RGBA color string
    const rgba = `rgba(${r}, ${g}, ${b}, ${alpha})`;

    return rgba;
  } catch (error) {
    console.log(error);
    return rgb;
  };
};
